/**
 * ReferencePicker — the control a REFERENCE field renders as: a typeahead over
 * the target table through the SQL firewall, read by its display column,
 * valued by its key (FEATURE-20261014-page-dsl-reference-field-is-picker-or-filter).
 *
 * The page named the field (`S.job`); the model said what it points at; this
 * control asks the firewall for rows as the person types, so a table of any
 * size works and the person sees only the rows the firewall grants them. The
 * same control is registered as the "reference" form widget, so an edit form
 * over the owning table gets it for its key column.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { FormWidgetService } from "../widgets/form-widget-service";
import { lookupReference, searchReference } from "./reference-query";
import type { ReferenceTarget, ReferenceValue } from "./reference-query";

const DEBOUNCE_MS = 200;

export function ReferencePicker(props: {
  target: ReferenceTarget;
  label: string;
  value: ReferenceValue | undefined;
  onChange: (v: ReferenceValue | undefined) => void;
  disabled?: boolean;
  /** stable id for tests and labels: the field's name */
  name?: string;
}) {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState<ReferenceValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  // Search as the person types; the latest reply wins, earlier ones are dropped.
  useEffect(() => {
    if (!open) return;
    const mine = ++seq.current;
    const t = setTimeout(() => {
      setLoading(true);
      searchReference(props.target, input)
        .then((rows) => {
          if (seq.current === mine) setOptions(rows);
        })
        .catch((e: unknown) => console.error("ReferencePicker | search |", e))
        .finally(() => {
          if (seq.current === mine) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, input, props.target]);

  // The selected value is always an option, so MUI never warns about a value outside the list.
  const all = useMemo(
    () => (props.value && !options.some((o) => o.key === props.value!.key) ? [props.value, ...options] : options),
    [options, props.value],
  );

  return (
    <Autocomplete<ReferenceValue, false, false, false>
      className="reference-picker"
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={all}
      value={props.value ?? null}
      onChange={(_e, v) => props.onChange(v ?? undefined)}
      inputValue={input}
      onInputChange={(_e, v, reason) => {
        // "reset" is MUI putting the picked option's text in the box; keep it in
        // step, or the box keeps showing what was typed to find it
        if (reason === "input" || reason === "reset") setInput(v);
        else if (reason === "clear") setInput("");
      }}
      getOptionLabel={(o) => o.text}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      filterOptions={(x) => x} // the firewall filtered already
      loading={loading}
      disabled={props.disabled}
      noOptionsText={input ? `No ${props.label.toLowerCase()} matches "${input}"` : `No ${props.label.toLowerCase()}`}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          name={props.name}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, "data-testid": props.name ? `reference-${props.name}` : undefined },
          }}
        />
      )}
    />
  );
}

/**
 * The "reference" form widget: `{ type: "widget", widget: "reference", config:
 * { table, key, display } }` edits a key column through the same picker. The
 * form value is the key string (what the column stores); the text is looked
 * up for display.
 */
function ReferenceWidget(props: {
  formItem: { name: string; label?: string; disabled?: boolean; config?: Record<string, unknown> };
  formData: Record<string, unknown>;
  setValue: (v: unknown) => void;
}) {
  const cfg = props.formItem.config ?? {};
  const target: ReferenceTarget = { table: String(cfg["table"]), key: String(cfg["key"] ?? "uid"), display: String(cfg["display"] ?? "name") };
  const raw = props.formData[props.formItem.name];
  const key = raw == null || raw === "" ? undefined : String(raw);
  // The text for the stored key, looked up once per key; until it arrives the key itself is shown.
  const [looked, setLooked] = useState<ReferenceValue | undefined>();
  useEffect(() => {
    if (key === undefined || looked?.key === key) return;
    let cancelled = false;
    lookupReference(target, key)
      .then((v) => {
        if (!cancelled) setLooked(v ?? { key, text: key });
      })
      .catch(() => {
        if (!cancelled) setLooked({ key, text: key });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, target.table, target.key, target.display]);
  const value = key === undefined ? undefined : looked?.key === key ? looked : { key, text: key };
  return (
    <ReferencePicker
      target={target}
      label={props.formItem.label ?? props.formItem.name}
      name={props.formItem.name}
      value={value}
      disabled={props.formItem.disabled}
      onChange={(v) => {
        setLooked(v);
        props.setValue(v?.key ?? null);
      }}
    />
  );
}

FormWidgetService.get().register({
  name: "reference",
  render: (p) => <ReferenceWidget formItem={p.formItem} formData={p.formData} setValue={p.setValue} />,
});
